import boardFormat from "@/prompts/whiteboard_stream_schema.json";
import type { StreamSnapshot } from "./controller";
import { textLimit } from "./textLimits";

type Schema = {
  type?: string;
  anyOf?: Schema[];
  properties?: Record<string, Schema>;
  required?: string[];
  items?: Schema;
  enum?: string[];
  pattern?: string;
  minLength?: number;
  maxLength?: number;
  minItems?: number;
  maxItems?: number;
  minimum?: number;
  maximum?: number;
};

// Validate the subset used by the shared owned asset before removing nulls.
function matches(value: unknown, schema: Schema): boolean {
  if (schema.anyOf) return schema.anyOf.some((child) => matches(value, child));
  if (schema.type === "null") return value === null;
  if (schema.type === "object") {
    if (!value || typeof value !== "object" || Array.isArray(value)) return false;
    const item = value as Record<string, unknown>;
    return (
      Object.keys(item).length === schema.required!.length &&
      schema.required!.every((key) => Object.hasOwn(item, key)) &&
      Object.entries(schema.properties!).every(([key, child]) => matches(item[key], child))
    );
  }
  if (schema.type === "array")
    return (
      Array.isArray(value) &&
      value.length >= schema.minItems! &&
      value.length <= schema.maxItems! &&
      value.every((child) => matches(child, schema.items!))
    );
  if (schema.type === "string")
    return (
      typeof value === "string" &&
      [...value].length >= (schema.minLength ?? 0) &&
      [...value].length <= (schema.maxLength === undefined ? 8192 : textLimit(schema.maxLength)) &&
      (!schema.enum || schema.enum.includes(value)) &&
      (!schema.pattern || new RegExp(schema.pattern).test(value))
    );
  if (schema.type === "integer")
    return (
      typeof value === "number" &&
      Number.isInteger(value) &&
      value >= schema.minimum! &&
      value <= schema.maximum!
    );
  return schema.type === "boolean" && typeof value === "boolean";
}

function withoutNulls(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(withoutNulls);
  if (value && typeof value === "object")
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, item]) => item !== null)
        .map(([key, item]) => [key, withoutNulls(item)])
    );
  return value;
}

export class StructuredFramer {
  private pending = "";
  private bytes = 0;
  private state = "prefix";
  private offset = 0;
  private depth = 0;
  private count = 0;
  private quoted = false;
  private escaped = false;

  *feed(text: string): Generator<string> {
    this.bytes += new TextEncoder().encode(text).length;
    if (this.bytes > 96 * 1024) throw new Error("Stream budget");
    this.pending += text;
    while (true) {
      if (this.state === "prefix") {
        const prefix = /^\s*\{\s*"records"\s*:\s*\[/.exec(this.pending);
        if (!prefix) {
          if (this.pending.length > 80) throw new Error("Structured envelope");
          break;
        }
        this.pending = this.pending.slice(prefix[0].length);
        this.state = "item";
      }
      if (["item", "delimiter", "close", "done"].includes(this.state)) {
        this.pending = this.pending.trimStart();
        if (!this.pending) break;
        const char = this.pending[0];
        if (this.state === "delimiter") {
          if (char === ",") this.state = "next_item";
          else if (char === "]") this.state = "close";
          else throw new Error("Structured envelope");
          this.pending = this.pending.slice(1);
          continue;
        }
        if (this.state === "close") {
          if (char !== "}") throw new Error("Structured envelope");
          this.state = "done";
          this.pending = this.pending.slice(1);
          continue;
        }
        if (this.state === "done") throw new Error("Structured envelope");
        if (char === "]") {
          this.state = "close";
          this.pending = this.pending.slice(1);
          continue;
        }
        if (char !== "{") throw new Error("Structured envelope");
        this.state = "object";
      }
      if (this.state === "next_item") {
        this.pending = this.pending.trimStart();
        if (!this.pending) break;
        if (this.pending[0] !== "{") throw new Error("Structured envelope");
        this.state = "object";
      }
      if (this.state === "object") {
        let found = false;
        while (this.offset < this.pending.length) {
          const char = this.pending[this.offset];
          if (this.quoted) {
            if (this.escaped) this.escaped = false;
            else if (char === "\\") this.escaped = true;
            else if (char === '"') this.quoted = false;
          } else if (char === '"') this.quoted = true;
          else if (char === "{" || char === "[") this.depth++;
          else if (char === "}" || char === "]") this.depth--;
          this.offset++;
          if (this.depth === 0) {
            const raw = this.pending.slice(0, this.offset);
            this.pending = this.pending.slice(this.offset);
            this.offset = 0;
            this.count++;
            this.state = "delimiter";
            if (new TextEncoder().encode(raw).length > 8192) throw new Error("Line budget");
            if (this.count > boardFormat.schema.properties.records.maxItems)
              throw new Error("Structured record count");
            const record: unknown = JSON.parse(raw);
            if (!matches(record, boardFormat.schema.properties.records.items as unknown as Schema))
              throw new Error("Structured record schema");
            yield JSON.stringify(withoutNulls(record));
            found = true;
            break;
          }
        }
        if (!found) break;
      }
    }
    if (new TextEncoder().encode(this.pending).length > 8192) throw new Error("Line budget");
  }

  finish() {
    if (this.state !== "done" || this.pending.trim()) throw new Error("Structured envelope");
  }
}

export function validateCompactBoard(snapshot: Pick<StreamSnapshot, "spec" | "exercises">) {
  const tasks = Object.values(snapshot.exercises).map((unit) => unit.spec);
  if (tasks.length > 1) throw new Error("Compact task count");
  const reading = tasks.some((spec) => spec.kind === "reading_story");
  const content = Object.entries(snapshot.spec.elements)
    .filter(([id, value]) => id !== "board" && value.type !== "Exercise")
    .map(([, value]) => value);
  if (content.length > 2 || content.filter((v) => v.type === "Heading").length > 1)
    throw new Error("Compact support budget");
  if (
    tasks.length &&
    !reading &&
    content.some((v) => v.type === "Text" && String(v.props.text).length > textLimit(120))
  )
    throw new Error("Compact text budget");
  for (const task of tasks) {
    let total = 0;
    const bound = (value: unknown, field = "") => {
      if (typeof value === "string" && !["kind", "id", "type"].includes(field)) {
        const maximum =
          (
            { story: 600, title: 60, hint: 90, explanation: 160, note: 90 } as Record<
              string,
              number
            >
          )[field] ?? 180;
        if ([...value].length > textLimit(maximum)) throw new Error("Compact text budget");
        total += [...value].length;
      } else if (Array.isArray(value)) {
        const maximum =
          (
            {
              tiles: 8,
              pool: 8,
              blocks: 1,
              rows: 2,
              cells: 2,
              pairs: 2,
              leftItems: 3,
              rightItems: 3,
              phrases: 3,
            } as Record<string, number>
          )[field] ?? 4;
        if (value.length > maximum) throw new Error("Compact collection budget");
        value.forEach((item) => bound(item));
      } else if (value && typeof value === "object") {
        Object.entries(value).forEach(([key, item]) => bound(item, key));
      }
    };
    bound(task);
    if (total > textLimit(800)) throw new Error("Compact text budget");
  }
}
